import React, { useEffect } from "react";
import { Layout, Button, message, Modal, Space } from "antd";
import { useSearchParams, useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import { DragDropProvider } from "@dnd-kit/react";
import { loadPage } from "./loadPage";
import {
    getRules,
    getYear,
    getDivisionId,
    getLookups,
    getEditingCondition,
    getDirty,
    hasValidationErrors,
} from "./modules/EarlyBirdOfferBuilder";
import {
    addCondition,
    updateCondition,
    removeCondition,
    setEditingConditionUiId,
    updateRule,
    removeRule,
    addRule,
} from "./actions";
import {
    createDefaultCondition,
    getFieldLabel,
    isFieldLibraryItemId,
    FieldLibraryItemId,
} from "./fieldLibrary";
import { rulesToDto } from "./validation";
import { upsertEarlyBirdOffer } from "app/shared/api";
import { FieldLibrary } from "./components/FieldLibrary";
import { RuleCard } from "./components/RuleCard";
import { ConditionEditorSidebar } from "./components/ConditionEditorSidebar";
import { EarlyBirdRuleWithUiId } from "./actionTypes";
import { getSelectedDivisionId } from "app/pages/AgentPricing/utils/division";
import { PublishedEventType, useSignalREvent } from "app/helpers/signalRHelper";

const { Content, Sider } = Layout;

interface StateProps {
    rules: EarlyBirdRuleWithUiId[];
    year: number;
    divisionId: number;
    lookups: ReturnType<typeof getLookups>;
    editingCondition: ReturnType<typeof getEditingCondition>;
    dirty: boolean;
    hasValidationErrors: boolean;
}

interface DispatchProps {}

const PageComponent: React.FC<StateProps & DispatchProps> = ({
     rules,
     year,
     divisionId,
     lookups,
     editingCondition,
     dirty,
     hasValidationErrors,
 }) => {
    const hasAnyCondition = rules.some(
        (r) => r.include.length + r.exclude.length > 0
    );
    const saveDisabled = !dirty || hasValidationErrors || !hasAnyCondition;

    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [pendingFieldId, setPendingFieldId] =
        React.useState<FieldLibraryItemId | null>(null);
    const [saving, setSaving] = React.useState(false);

    const navigateBack = React.useCallback(() => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        navigate("/early-bird-offer");
    }, [navigate]);

    const handleExit = React.useCallback(() => {
        if (!dirty) {
            navigateBack();
            return;
        }

        Modal.confirm({
            title: "Unsaved changes",
            content:
                "You have unsaved changes. Are you sure you want to leave this page?",
            okText: "Leave",
            cancelText: "Stay",
            onOk: navigateBack,
        });
    }, [dirty, navigateBack]);

    useEffect(() => {
        if (!dirty) {
            return;
        }

        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = "";
        };

        window.addEventListener("beforeunload", onBeforeUnload);
        return () =>
            window.removeEventListener("beforeunload", onBeforeUnload);
    }, [dirty]);

    const handleSave = () => {
        if (saveDisabled || saving) {
            return;
        }

        setSaving(true);
        const dto = rulesToDto(rules);

        upsertEarlyBirdOffer(divisionId, year, dto)
            .then(() => {
                message.success("Early Bird Offer saved.");
                navigate("/early-bird-offer", {
                    state: { unpublishedChanges: true },
                });
            })
            .catch((err) => {
                message.error(err?.message ?? "Failed to save.");
            })
            .finally(() => setSaving(false));
    };

    const yearParam = searchParams.get("year") ?? "";
    const loadBuilderData = React.useCallback(() => {
        const parsed = yearParam ? parseInt(yearParam, 10) : new Date().getFullYear();
        const yearToLoad = !isNaN(parsed) ? parsed : new Date().getFullYear();
        loadPage(dispatch, { year: String(yearToLoad) }).catch((err) => {
            message.error(err?.message ?? "Failed to load Early Bird Offer.");
        });
    }, [dispatch, yearParam]);

    const refreshGridEvent = React.useCallback(async () => {
        // Avoid overwriting in-progress edits; user can manually refresh after saving/cancelling.
        if (dirty) return;
        loadBuilderData();
    }, [dirty, loadBuilderData]);

    useSignalREvent(
        [PublishedEventType.EarlyBirdEventPublished],
        refreshGridEvent,
        divisionId
    );

    useEffect(() => {
        loadBuilderData();
    }, [loadBuilderData]);

    useEffect(() => {
        const onDivisionChanged = () => {
            loadBuilderData();
        };

        window.addEventListener("selectedDivisionChanged", onDivisionChanged);
        return () =>
            window.removeEventListener("selectedDivisionChanged", onDivisionChanged);
    }, [loadBuilderData]);

    const tryAddCondition = (
        ruleIndex: number,
        lane: "include" | "exclude",
        fieldId: FieldLibraryItemId
    ) => {
        const rule = rules[ruleIndex];
        if (!rule) return;

        const includeFields = new Set(rule.include.map((c) => c.field));
        const excludeFields = new Set(rule.exclude.map((c) => c.field));
        const laneSet = lane === "include" ? includeFields : excludeFields;
        const otherSet = lane === "include" ? excludeFields : includeFields;

        if (laneSet.has(fieldId)) {
            message.warning(
                `You already added "${getFieldLabel(fieldId)}" to ${
                    lane === "include" ? "Include" : "Exclude"
                } for this rule.`
            );
            return;
        }

        if (otherSet.has(fieldId)) {
            message.warning(
                `You can't add "${getFieldLabel(
                    fieldId
                )}" to both Include and Exclude for the same rule.`
            );
            return;
        }

        const condition = createDefaultCondition(fieldId);
        dispatch(addCondition(ruleIndex, lane, condition));
    };

    const handleDragEnd = (event: any) => {
        const op = event?.operation;

        if (!op || event.canceled) {
            return;
        }

        const source = op.source;
        const target = op.target;

        if (
            !source?.id ||
            !target?.id ||
            typeof source.id !== "string" ||
            typeof target.id !== "string"
        ) {
            return;
        }

        if (
            !source.id.startsWith("library-") ||
            !target.id.startsWith("lane-")
        ) {
            return;
        }

        const rawFieldId =
            source.data?.fieldId ?? source.id.replace("library-", "");

        if (!isFieldLibraryItemId(rawFieldId)) {
            return;
        }

        const ruleIndex =
            target.data?.ruleIndex ?? parseInt(target.id.split("-")[1], 10);

        const lane: "include" | "exclude" =
            target.data?.lane ??
            (target.id.includes("include") ? "include" : "exclude");

        if (isNaN(ruleIndex) || ruleIndex < 0 || ruleIndex >= rules.length) {
            return;
        }

        tryAddCondition(ruleIndex, lane, rawFieldId);
    };

    const handleFieldLibraryClick = (fieldId: FieldLibraryItemId) => {
        setPendingFieldId(fieldId);
        message.info(
            `Click an Include or Exclude lane to add "${getFieldLabel(
                fieldId
            )}".`
        );
    };

    const handleLaneClick = (
        ruleIndex: number,
        lane: "include" | "exclude"
    ) => {
        if (!pendingFieldId) {
            return;
        }

        tryAddCondition(ruleIndex, lane, pendingFieldId);
        setPendingFieldId(null);
    };

    return (
        <DragDropProvider onDragEnd={handleDragEnd}>
            <Layout>
                <Content style={{ padding: 24, background: "#fff" }}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 16,
                            gap: 12,
                            flexWrap: "wrap",
                        }}
                    >
                        <Space>
                            <Button onClick={handleExit}>Back</Button>
                            <h1 style={{ margin: 0 }}>Early Bird Offer</h1>
                        </Space>

                        <Space>
                            <Button onClick={handleExit}>Cancel</Button>
                            <Button
                                type="primary"
                                onClick={handleSave}
                                disabled={saveDisabled || saving}
                                loading={saving}
                            >
                                Save
                            </Button>
                        </Space>
                    </div>

                    {rules.map((rule, ruleIndex) => (
                        <RuleCard
                            key={ruleIndex}
                            ruleIndex={ruleIndex}
                            rule={rule}
                            pricingYear={year}
                            lookups={lookups}
                            onUpdateName={(value) =>
                                dispatch(updateRule(ruleIndex, { name: value }))
                            }
                            onUpdateEndDate={(value) =>
                                dispatch(
                                    updateRule(ruleIndex, {
                                        earlyBirdEndDate: value,
                                    })
                                )
                            }
                            onRemoveRule={() => dispatch(removeRule(ruleIndex))}
                            onRemoveCondition={(lane, uiId) =>
                                dispatch(removeCondition(ruleIndex, lane, uiId))
                            }
                            onEditCondition={(_lane, condition) =>
                                dispatch(
                                    setEditingConditionUiId(
                                        (condition as any).uiId
                                    )
                                )
                            }
                            onLaneClick={handleLaneClick}
                        />
                    ))}

                    <Button
                        type="dashed"
                        onClick={() => dispatch(addRule())}
                        style={{ marginBottom: 24 }}
                    >
                        + Add rule
                    </Button>
                </Content>

                <Sider
                    width={320}
                    style={{
                        background: "#fff",
                        borderLeft: "1px solid #f0f0f0",
                    }}
                >
                    {editingCondition ? (
                        <ConditionEditorSidebar
                            condition={editingCondition.condition}
                            ruleIndex={editingCondition.ruleIndex}
                            lane={editingCondition.lane}
                            lookups={lookups}
                            onSave={(condition) => {
                                dispatch(
                                    updateCondition(
                                        editingCondition.ruleIndex,
                                        editingCondition.lane,
                                        (condition as any).uiId,
                                        condition
                                    )
                                );
                                dispatch(setEditingConditionUiId(null));
                            }}
                            onClose={() =>
                                dispatch(setEditingConditionUiId(null))
                            }
                        />
                    ) : (
                        <FieldLibrary onFieldClick={handleFieldLibraryClick} />
                    )}
                </Sider>
            </Layout>
        </DragDropProvider>
    );
};

const mapStateToProps = (state: any): StateProps => ({
    rules: getRules(state),
    year: getYear(state),
    divisionId: getDivisionId(state),
    lookups: getLookups(state),
    editingCondition: getEditingCondition(state),
    dirty: getDirty(state),
    hasValidationErrors: hasValidationErrors(state),
});

export const Page = connect(mapStateToProps)(PageComponent);